import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { CarlineWithOfertas } from '../../../services/ofertas-service/models/ofertas';
import { Line, ModelTitleBackgroundContainer, TextContainer } from './ui/model-title-background-styles';

interface ModelTitleBackgroundProps {
	text: string;
	isShowroom: boolean;
	className?: string;
}

const ModelTitleBackground = ({ text, isShowroom }: ModelTitleBackgroundProps): React.ReactElement => {
	const mergeLastTwoWords = (text: string): string[] => {
		if (!text) return [];
		const words = text.split(' ');
		if (words.length < 2) return words;

		// Si el texto completo tiene 8 caracteres o menos (sin contar espacios), no dividir
		const textWithoutSpaces = text.replace(/\s/g, '');
		if (textWithoutSpaces.length <= 8) return [text];

		// Si hay 3 o más palabras, dividir dejando la última palabra sola
		if (words.length >= 3) {
			const firstPart = words.slice(0, -1).join(' ');
			const lastWord = words[words.length - 1];
			return [firstPart, lastWord];
		}

		// Si hay 2 palabras, dividir normalmente
		return words;
	};

	const words = mergeLastTwoWords(text);
	const hasSpace = (text: string): boolean => text.includes(' ');
	const hasElement = (text: string): boolean => text.includes('.') || text.includes('‑');

	const [fontSizes, setFontSizes] = React.useState<number[]>([]);
	const [lineHeight, setLineHeight] = React.useState(0);

	React.useEffect(() => {
		const handleResizeAndCalculation = () => {
			const sizes = words.map((word) => {
				const testDiv = document.createElement('div');
				testDiv.style.visibility = 'hidden';
				testDiv.style.position = 'absolute';
				testDiv.style.whiteSpace = 'nowrap';
				testDiv.style.fontSize = '100px'; // Empezamos con un tamaño grande
				testDiv.innerHTML = word;
				document.body.appendChild(testDiv);

				const width = testDiv.offsetWidth;
				let fontSize: number = 0;

				const widthWindow = window.innerWidth;
				if (widthWindow < 960) {
					fontSize = (widthWindow / width) * 65;
				} else if (widthWindow >= 960 && widthWindow < 1200) {
					fontSize = (widthWindow / width) * 47;
				} else {
					fontSize = (widthWindow / width) * 40;
				}

				document.body.removeChild(testDiv);
				return fontSize;
			});

			setFontSizes(sizes);
			// Calcular el interlineado en función del tamaño de la fuente más grande
			const maxFontSize = Math.max(...sizes);
			setLineHeight(maxFontSize * 0.75); // Ejemplo: 20% mayor que el tamaño de la fuente más grande
		};

		handleResizeAndCalculation();

		// listener por si redimensionan la ventana dinámicamente
		window.addEventListener('resize', handleResizeAndCalculation);
		return () => window.removeEventListener('resize', handleResizeAndCalculation);
	}, [text]);

	return (
		<TextContainer isShowroom={isShowroom}>
			{words.map((word, index) => (
				<Line
					className="model-title-background__line"
					key={index}
					fontSize={fontSizes[index]}
					lineHeight={lineHeight}
					isFirst={index === 0}
					hasSpace={hasSpace(word)}
					hasElement={hasElement(word)}
					numberOfWords={words.length}
					numberOfLetters={word.length}
					isShowroom={isShowroom}
				>
					{word.toLocaleUpperCase()}
				</Line>
			))}
		</TextContainer>
	);
};

export const TitleBackground = ({
	isShowroom,
	activeCarlineIndex,
	carlines,
}: {
	isShowroom: boolean;
	activeCarlineIndex: number;
	carlines: CarlineWithOfertas[];
}) => {
	return (
		<ModelTitleBackgroundContainer className="model-title-background-container" isShowroom={isShowroom}>
			{/* mode="wait" asegura que el título viejo desaparezca antes de que entre el nuevo */}
			<AnimatePresence mode="wait">
				<motion.div
					key={activeCarlineIndex} // detecta el cambio de coche para relanzar el fade
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.15, ease: 'linear' }}
					style={{ width: '100%', height: '100%' }}
				>
					<ModelTitleBackground
						className="model-title-background"
						text={carlines[activeCarlineIndex]?.title.replace(/-/g, '\u2011')}
						isShowroom={isShowroom}
					/>
				</motion.div>
			</AnimatePresence>
		</ModelTitleBackgroundContainer>
	);
};
